import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import parse from 'html-react-parser';
import { Col, Container, Row } from 'react-bootstrap';

import Button from '../../../ui-core/Button/Button';
import LazyImage from '../../../ui-core/Image';
import useWindowWidth from '../../../hooks/useWindowWidth';
import { getDisplayType } from '../../../utils/helpers';
import { IMAGE_CONSTANT, TEXT_CONSTANT } from '../../../constants/app';

import './style.css';

const sections = [
  {
    image: IMAGE_CONSTANT.barefeet1,
    placeholderImage: IMAGE_CONSTANT.barefeet1Low,
    webpImage: IMAGE_CONSTANT.barefeet1Webp,
    description:
      "Oh, baby's feet. Aren't they beautiful? We are born with the ability to spread our toes and pull the big toe back as a natural reflex called the Babinski Reflex.<br /> <b>Can you spread your toes like this now?</b><br /> Many people can't even access the slightest movement to most of their toes when they try to spread them, especially the pinky toe. This is where that old saying comes back... <b>if you don't use it, you lose it.</b><br /> You see, we have important muscles surrounding these toes, called your <b>intrinsic foot muscles</b>, which aide in strength and support of the foot. Without the ability to spread the toes, we reduce access to our natural foot support and therefore, lack adequate foot strength.",
  },
  {
    image: IMAGE_CONSTANT.barefeet2,
    placeholderImage: IMAGE_CONSTANT.barefeet2Low,
    webpImage: IMAGE_CONSTANT.barefeet2Webp,
    description:
      "As you look at this photo, you might think, \"But my shoes don't look like that.\" Unfortunately, many do. Shoes have been designed for style and aesthetic, not for function.<br /><b>A wide toe box does NOT equal a wide toe area.</b><br />It will create more space at what is called our <b>forefoot</b> (ball of the foot area) but does <b>not</b> create more space for a natural toe spread at the <b>top</b> of the shoe where it's needed.<br/><br/>Beyond that, <b>we've been lied to!</b><br/><br/> <b>More cushion, more arch support actually weakens our foot.</b><br />We end up relying on external support to do the work that our foot muscles are naturally supposed to do on their own. So, we end up losing the connection to spread our toes or strengthen our feet.",
  },
  {
    image: IMAGE_CONSTANT.barefeet3,
    placeholderImage: IMAGE_CONSTANT.barefeet3Low,
    webpImage: IMAGE_CONSTANT.barefeet3Webp,
    description:
      "Now that you've seen what shoes have done to our feet over the years, let's discuss the true cause of <b>bunions.</b><br /><b>A bunion is NOT the result of the big toe moving in toward the other toes.</b><br />This big toe moves as a result of your <b>first metatarsal bone</b>, the bone you see connecting into the big toe, <b>moving out.</b><br />This is caused by <b>instability</b> at the bottom of the foot (a lack of strength). So, are orthotics, arch supports and overly cushioned shoes really helping our foot? Or, are they creating an issue of dependence and merely weakening our feet? Now, there are cases when orthotics, various shoe support and more cushioned shoes are appropriate. However, in general, we need <b>less dependence on external sources</b> and <b>more understanding of how to use our own internal support - our feet!</b><br /> Though <b>we can't reverse a bunion</b>, we can certainly reduce the pressure placed on the cartilage at the big toe joint <b>(arthritis)</b>, improve strength and function of the feet and ultimately, <b>reduce pain and symptoms.</b><br />Are you ready to learn how to do this?",
  },
  {
    image: IMAGE_CONSTANT.barefeet4,
    placeholderImage: IMAGE_CONSTANT.barefeet4Low,
    webpImage: IMAGE_CONSTANT.barefeet4Webp,
    description:
      "For those who feel pain either at the bottom of the foot, the heel, up the achilles or at the calf or shin of the lower leg, it's important to remember that <b>where you feel the pain</b> is the end result symptom that lets us know the diagnosis. However, that feeling of pain does not tell us the root cause of that pain or injury.<br />For example, <b>the strongest predictor of plantar heel pain, or plantar fasciitis, is limited ankle dorsiflexion</b> (shin moving forward over toes) with the leg extended.<br />This usually becomes restricted due to <b>tight calves</b> and <b>poor ankle mobility</b>.<br />This means the plantar fascia is not necessarily the issue. So, <b>rolling, icing or only addressing the bottom of the foot might temporarily relieve some symptoms you feel but there is a high chance the pain will come back unless the root cause issue is properly addressed.</b>",
  },
  {
    image: IMAGE_CONSTANT.barefeet5,
    placeholderImage: IMAGE_CONSTANT.barefeet5Low,
    webpImage: IMAGE_CONSTANT.barefeet5Webp,
    description:
      "<b>Ready to learn more?</b><br />Each day of the course, you will not only be receiving a <b>detailed video</b> of how to <b>properly assess</b> movement, restrictions and weaknesses, but you will also be receiving <b>additional educational content</b> to read through, learn and <b>finally understand your body!</b><br />This course was created with those who have only been told by doctors <b>what to do</b> without ever a clear understanding of <b>why</b> or what is truly going on deeper at the <b>root cause</b> level.<br /> We'll be reviewing the following diagnoses: <b>bunions, plantar fasciitis, metatarsalgia, Morton's neuroma, flat feet, high arches, over-pronation, shin splints, posterior tibialis tendinitis, achilles tendinitis</b>, and we'll even end with reviewing the different systems that play a role in <b>balance!</b><br /><b>Understanding the body is a lifelong journey</b>. The only way to keep moving forward is through curiosity and exploration.",
  },
];

const DetailedProblem = props => {
  const displayType = getDisplayType(useWindowWidth());
  const isMobileScreen = displayType === 'mobile';
  const isIpadScreen = displayType === 'ipad';
  const isDesktopScreen = displayType === 'desktop';
  const handleClick = () => {
    if (props.onClick) {
      props.onClick(
        TEXT_CONSTANT.SIGN_UP_HERE,
        'isSignup',
        TEXT_CONSTANT.WHY_FOOT_PAIN,
      );
    }
  };
  return (
    <div className="problem-detail">
      <h2 className="text-center">{TEXT_CONSTANT.WHY_FOOT_PAIN}</h2>
      {sections.map((section, index) => (
        <Container
          key={`section-${index + 1}`}
          className={clsx(
            'section-block',
            index === sections.length - 1 && 'hide-bottom-border pb-0',
          )}
        >
          <Row
            className={clsx(
              'd-flex',
              index % 2 !== 0 && 'flex-row-reverse',
              (isMobileScreen || isIpadScreen) && 'flex-column',
            )}
          >
            <Col xs={12} md={isDesktopScreen ? 6 : 12} className="d-flex">
              <LazyImage
                className={clsx(
                  'barefoot-section-image w-100',
                  index % 2 !== 0 &&
                    !(isMobileScreen || isIpadScreen) &&
                    'ms-auto',
                )}
                errorSrc={section.image}
                placeholderSrc={section.placeholderImage}
                src={section.webpImage}
              />
            </Col>
            <Col xs={12} md={isDesktopScreen ? 6 : 12} className="d-flex">
              <div className="section-text">{parse(section.description)}</div>
            </Col>
          </Row>
        </Container>
      ))}
      <Button
        onClick={handleClick}
        text={TEXT_CONSTANT.SIGN_UP_HERE}
        class="sign-up-button-new text-uppercase mt-5 ms-3"
      />
    </div>
  );
};

DetailedProblem.propTypes = {
  onClick: PropTypes.func,
};

export default DetailedProblem;
